// Submit pipeline for <sanctum-form>.
//
// Wraps fetch() with a 15-second AbortController timeout and maps HTTP
// status codes to the discriminated SubmitError union in schema.ts.
// Honeypot handling is built-in: if the configured honeypot field is
// non-empty, the submit resolves as a silent success (show success
// state, send nothing) so the bot isn't tipped off.

import type {
  ChallengeConfig,
  ChallengeResponse,
  FieldDefinition,
  FormSchemaResponse,
  SubmitError,
  SubmitErrorResponse,
  SubmitSuccessResponse,
} from "./schema";

export const SUBMIT_TIMEOUT_MS = 15_000;

export interface SubmitContext {
  endpointId: string;
  baseUrl: string;
  schema: FormSchemaResponse;
  payload: Record<string, string | string[]>;
  formEl?: HTMLFormElement | null;
  challengeToken?: string;
}

export type SubmitOutcome =
  | { kind: "success"; message: string; accepted: boolean }
  | { kind: "challenge"; challenge: ChallengeConfig; message: string }
  | { kind: "error"; error: SubmitError };

// Extract raw string values from a FormData so the payload is JSON-safe.
// Checkbox inputs only appear in FormData when checked, so we
// post-process known fields from the schema to guarantee a
// "false"/"true" value is present even for unchecked boxes when the
// embedder wants the key to always appear.
export function buildPayload(
  formEl: HTMLFormElement,
  schema: FormSchemaResponse,
): Record<string, string | string[]> {
  const multiSelectNames = new Set(
    schema.field_schema
      .filter((f) => f.type === "select" && f.multiple)
      .map((f) => f.name),
  );
  const orderedListNames = new Set(
    schema.field_schema
      .filter((f) => f.type === "ordered_list")
      .map((f) => f.name),
  );
  const checkboxGroupNames = new Set(
    schema.field_schema
      .filter((f) => f.type === "checkbox_group")
      .map((f) => f.name),
  );
  const payload: Record<string, string | string[]> = {};
  const data = new FormData(formEl);
  for (const [key, value] of data.entries()) {
    if (value instanceof File) continue;
    if (
      typeof value === "string" &&
      !multiSelectNames.has(key) &&
      !orderedListNames.has(key) &&
      !checkboxGroupNames.has(key)
    ) {
      payload[key] = value;
    }
  }

  for (const name of checkboxGroupNames) {
    payload[name] = data
      .getAll(name)
      .filter((v): v is string => typeof v === "string");
  }

  for (const name of orderedListNames) {
    const hidden = formEl.querySelector<HTMLInputElement>(
      `input[type="hidden"][name="${cssEscape(name)}"]`,
    );
    if (hidden && hidden.value) {
      try {
        const parsed = JSON.parse(hidden.value);
        payload[name] = Array.isArray(parsed) ? parsed : [];
      } catch {
        payload[name] = [];
      }
    }
  }

  // Multi-select: read selectedOptions directly so all selections are
  // captured even when FormData only yields the last one (happy-dom).
  for (const name of multiSelectNames) {
    const select = formEl.elements.namedItem(name);
    if (select instanceof HTMLSelectElement) {
      payload[name] = Array.from(select.selectedOptions).map((o) => o.value);
    }
  }

  // Unchecked checkboxes should land as "false" so the backend sees
  // the field rather than missing it. Only backfill schema-declared
  // checkboxes — we do not invent keys.
  for (const field of schema.field_schema) {
    if (
      (field.type === "checkbox" || field.type === "toggle") &&
      !(field.name in payload)
    ) {
      payload[field.name] = "false";
    }
    if (
      ((field.type === "select" && field.multiple) ||
        field.type === "ordered_list" ||
        field.type === "checkbox_group") &&
      !(field.name in payload)
    ) {
      payload[field.name] = [];
    }
  }
  return payload;
}

// Quick client-side required check so obvious omissions don't round-trip.
// Returns the name of the first missing field, or null.
export function firstMissingRequired(
  payload: Record<string, string | string[]>,
  schema: FormSchemaResponse,
): string | null {
  for (const field of schema.field_schema) {
    if (!field.required) continue;
    if (field.visible_if && !conditionMet(payload, field.visible_if)) continue;
    const v = payload[field.name];
    if (Array.isArray(v)) {
      if (v.length === 0) return field.name;
    } else if (v === undefined || v === null || v === "") {
      return field.name;
    }
    if (field.type === "checkbox" || field.type === "toggle") {
      if (v !== "true") return field.name;
    }
  }
  return null;
}

function conditionMet(
  payload: Record<string, string | string[]>,
  condition: { field: string; equals: string | boolean | string[] },
): boolean {
  const actual = payload[condition.field];
  const expected = condition.equals;
  if (Array.isArray(actual)) {
    if (Array.isArray(expected)) {
      return expected.some((v) => actual.includes(String(v)));
    }
    return actual.includes(String(expected));
  }
  if (Array.isArray(expected)) return expected.map(String).includes(String(actual));
  return actual === String(expected) || String(actual) === String(expected);
}

export function hasFileField(schema: FormSchemaResponse): boolean {
  return schema.field_schema.some((f) => f.type === "file");
}

export function isHoneypotTripped(
  payload: Record<string, string | string[]>,
  schema: FormSchemaResponse,
): boolean {
  const hp = payload[schema.settings.honeypot_field_name];
  return typeof hp === "string" && hp.trim() !== "";
}

function cssEscape(value: string): string {
  if (typeof (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS?.escape === "function") {
    return (globalThis as { CSS: { escape: (s: string) => string } }).CSS.escape(value);
  }
  return value.replace(/"/g, '\\"');
}

// Try to recover a field name from a 422 `detail` string of the shape
// "Field 'X' is required" or "Field 'X' is not a valid email address"
// or "Field 'X' exceeds maximum length of N". Returns null if the
// detail doesn't fit any known shape.
export function parseFieldFromDetail(
  detail: string,
  knownFields: FieldDefinition[],
): { field: string; kind: "required" | "invalid" } | null {
  const match = /Field '([^']+)'/.exec(detail);
  if (!match) return null;
  const name = match[1];
  if (!knownFields.some((f) => f.name === name)) return null;
  const kind = /is required/.test(detail) ? "required" : "invalid";
  return { field: name, kind };
}

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// POST the payload to /submit/{endpoint_id} and map the response into a
// SubmitOutcome. The caller renders the outcome.
export async function submitForm(ctx: SubmitContext): Promise<SubmitOutcome> {
  if (isHoneypotTripped(ctx.payload, ctx.schema)) {
    return {
      kind: "success",
      message: ctx.schema.settings.success_message ?? "Thank you for your submission.",
      accepted: false,
    };
  }

  const url = `${ctx.baseUrl.replace(/\/$/, "")}/submit/${ctx.endpointId}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

  const useMultipart = hasFileField(ctx.schema) && ctx.formEl != null;

  let response: Response;
  try {
    if (useMultipart) {
      const formData = new FormData(ctx.formEl!);
      if (ctx.challengeToken) {
        formData.set("_sf_challenge_token", ctx.challengeToken);
      }
      response = await fetch(url, {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
    } else {
      const payload = ctx.challengeToken
        ? { ...ctx.payload, _sf_challenge_token: ctx.challengeToken }
        : ctx.payload;
      response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    }
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === "AbortError") {
      return {
        kind: "error",
        error: {
          kind: "timeout",
          message: "The request took too long. Please try again.",
        },
      };
    }
    return {
      kind: "error",
      error: {
        kind: "network",
        message: "Could not reach the server. Please check your connection and try again.",
      },
    };
  }
  clearTimeout(timeoutId);

  if (response.ok) {
    const body = (await readJsonSafe(response)) as SubmitSuccessResponse | null;
    const message =
      (body && body.message) ||
      ctx.schema.settings.success_message ||
      "Thank you for your submission.";
    return { kind: "success", message, accepted: body?.accepted !== false };
  }

  const body = (await readJsonSafe(response)) as (SubmitErrorResponse & Partial<ChallengeResponse>) | null;
  const detail = body?.detail ?? "";

  if (
    response.status === 409
    && body?.challenge?.provider === "turnstile"
    && typeof body.challenge.site_key === "string"
    && typeof body.challenge.action === "string"
    && typeof body.challenge.cdata === "string"
  ) {
    return {
      kind: "challenge",
      challenge: body.challenge,
      message: detail || "Please complete the security verification to continue.",
    };
  }

  switch (response.status) {
    case 422: {
      // Paused form surfaces as 422 with a non-field detail per the
      // backend (public.py:107-110). Otherwise a field-scoped message.
      if (/paused/i.test(detail) || /not currently accepting/i.test(detail)) {
        return {
          kind: "error",
          error: { kind: "paused", message: "This form is not currently accepting submissions." },
        };
      }
      const parsed = parseFieldFromDetail(detail, ctx.schema.field_schema);
      if (parsed) {
        return {
          kind: "error",
          error: { kind: parsed.kind, field: parsed.field, message: detail },
        };
      }
      return {
        kind: "error",
        error: { kind: "unknown", message: detail || "The submission could not be validated." },
      };
    }
    case 429:
      return {
        kind: "error",
        error: {
          kind: "rate_limited",
          message: detail || "Too many submissions from this address. Please try again in a moment.",
        },
      };
    case 403:
      return {
        kind: "error",
        error: {
          kind: "origin_denied",
          message: detail || "Submissions from this site are not permitted.",
        },
      };
    case 404:
      return {
        kind: "error",
        error: {
          kind: "not_found",
          message: detail || "This form is unavailable. Please contact the site owner.",
        },
      };
    default:
      return {
        kind: "error",
        error: { kind: "unknown", message: detail || "Something went wrong. Please try again." },
      };
  }
}
